import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CalendarClock, Save, Loader2 } from "lucide-react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function SeasonalPaymentPanel() {
  const [months, setMonths] = useState([]);
  const [day, setDay] = useState(1);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/log/payment-preferences");
        setMonths(data.seasonal_months || []);
        setDay(data.payment_day_of_month || 1);
        setNotes(data.notes || "");
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = (i) => setMonths(months.includes(i) ? months.filter((m) => m !== i) : [...months, i].sort((a,b)=>a-b));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/log/payment-preferences", { seasonal_months: months, payment_day_of_month: day, notes });
      toast.success("Payment schedule updated.");
    } catch { toast.error("Could not save."); } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mv-card p-6" data-testid="seasonal-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
          <CalendarClock size={18}/>
        </div>
        <div>
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Seasonal Scheduling</span></div>
          <div className="font-display text-lg mv-green-text">Pay when your income arrives.</div>
        </div>
      </div>
      <p className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
        Select the months you want to be charged (harvest, bonus, tourism season, etc.). Reminders and receipts adjust automatically.
      </p>

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--mv-muted)" }}>Active months</div>
        <div className="flex flex-wrap gap-2">
          {MONTHS.map((label, idx) => {
            const m = idx + 1;
            const active = months.includes(m);
            return (
              <button
                key={m} type="button"
                onClick={() => toggle(m)}
                className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors ${active ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"}`}
                data-testid={`season-month-${m}`}
              >{label}</button>
            );
          })}
        </div>
      </div>

      <label className="block mt-5">
        <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Day of month</span>
        <input type="number" min="1" max="28" value={day} onChange={(e) => setDay(parseInt(e.target.value) || 1)}
          className="mt-1 w-24 px-3 py-2 rounded-xl border mv-hairline text-[13.5px]" data-testid="season-day"/>
      </label>

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)"
        className="mt-4 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]" />

      <button onClick={save} disabled={saving} className="btn-green mt-4" data-testid="season-save">
        {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
        {saving ? "Saving…" : "Save Schedule"}
      </button>
    </div>
  );
}
